// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
// A custom day-view renderer for `DatePicker` that shows two month grids
// side by side. Useful when the user frequently picks dates that span the
// end of one month into the next, such as ship and delivery dates.
import { Box, Stack } from '@mui/material';
import { DateView } from '@mui/x-date-pickers-pro';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import {
  DateViewRendererProps,
  renderDateViewCalendar,
} from '@mui/x-date-pickers/dateViewRenderers';
import { DateTime } from 'luxon';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

// Context that supplies the externally-tracked value to the `slots.day`
// component. The value flows through context so the day component's identity
// stays constant across selections — preserving every PickersDay instance
// rather than unmounting/remounting all ~84 cells on each click — while
// still re-rendering them whenever the selected value changes.
const SelectedValueContext = createContext<DateTime | null>(null);

// `slots.day` component whose `selected` state is derived from the external
// value supplied via context. Defined once at module scope so its component
// identity is stable; React reuses existing PickersDay instances across
// renders instead of remounting them.
function SelectedDay(props: PickersDayProps<DateTime>): JSX.Element {
  const externalValue = useContext(SelectedValueContext);
  const isSelected =
    externalValue != null &&
    externalValue.isValid &&
    externalValue.hasSame(props.day, 'day');
  return <PickersDay {...props} selected={isSelected} />;
}

// No-op `onViewChange` handler used on the inner `DateCalendar`s. The outer
// `DatePicker` owns view switching; the inner calendars are forced to the
// `day` view and must not propagate header clicks back up — doing so would
// switch the outer picker away from the day view, skipping `TwoMonthDayView`
// on the next render and violating React's rules of hooks (this renderer is
// invoked as a plain function and uses `useState`/`useEffect`).
function noopViewChange(): void {
  // Intentionally empty.
}

/**
 * Custom `viewRenderers.day` for MUI `DatePicker` that renders two synchronized
 * month grids side by side. Pass via `viewRenderers={{ day: TwoMonthDayView }}`.
 *
 * The anchor month is the month displayed on the left; the right calendar
 * always shows anchor + 1 month. Initialized from the current value, the
 * picker's referenceDate, or "today" — in that order of preference.
 *
 * @param props The props supplied by MUI's day-view renderer slot.
 * @returns Two `DateCalendar` instances rendered side by side.
 */
export function TwoMonthDayView(
  props: DateViewRendererProps<DateTime, DateView>
): JSX.Element {
  const [anchorMonth, setAnchorMonth] = useState<DateTime>(() => {
    const seed =
      props.value && props.value.isValid
        ? props.value
        : (props.referenceDate ?? DateTime.now());
    return seed.startOf('month');
  });

  // Mirror the latest `anchorMonth` and `props.value` into refs so the effect
  // below can read them without listing either as a dependency. Adding
  // `anchorMonth` would create a feedback loop (every navigation step would
  // retrigger the visibility check); adding `props.value` would refire on
  // every parent render that passes a fresh DateTime instance for the same
  // moment. The effect is scoped to the only meaningful trigger: the
  // numeric millis representation of the external value changing.
  const anchorMonthRef = useRef(anchorMonth);
  anchorMonthRef.current = anchorMonth;
  const valueRef = useRef(props.value);
  valueRef.current = props.value;

  // When the form value changes externally to a date that is not currently
  // visible (e.g. from a shortcut button), navigate to that month.
  const valueMillis =
    props.value && props.value.isValid ? props.value.toMillis() : null;
  useEffect(() => {
    const value = valueRef.current;
    if (value && value.isValid) {
      const valueMonth = value.startOf('month');
      const currentAnchor = anchorMonthRef.current;
      const isVisible =
        valueMonth.equals(currentAnchor) ||
        valueMonth.equals(currentAnchor.plus({ months: 1 }));
      if (!isVisible) {
        setAnchorMonth(valueMonth);
      }
    }
  }, [valueMillis]);

  const rightMonth = anchorMonth.plus({ months: 1 });

  // Force the inner calendars to the `day` view only and intercept any
  // `onViewChange` MUI fires from header clicks so it does not bubble back
  // to the outer `DatePicker`. See `noopViewChange` above for the rationale.
  const sharedCalendarProps: DateViewRendererProps<DateTime, DateView> = {
    ...props,
    value: null,
    defaultValue: null,
    view: 'day',
    views: ['day'],
    onViewChange: noopViewChange,
    slots: { ...props.slots, day: SelectedDay },
  };

  const handleLeftMonthChange = (newMonth: DateTime) => {
    setAnchorMonth(newMonth.startOf('month'));
    props.onMonthChange?.(newMonth);
  };

  const handleRightMonthChange = (newMonth: DateTime) => {
    setAnchorMonth(newMonth.minus({ months: 1 }).startOf('month'));
    props.onMonthChange?.(newMonth);
  };

  return (
    <SelectedValueContext.Provider value={props.value ?? null}>
      <Stack direction="row" spacing={1}>
        <Box key={`left-${anchorMonth.toMillis()}`}>
          {renderDateViewCalendar({
            ...sharedCalendarProps,
            referenceDate: anchorMonth,
            onMonthChange: handleLeftMonthChange,
          })}
        </Box>
        <Box key={`right-${rightMonth.toMillis()}`}>
          {renderDateViewCalendar({
            ...sharedCalendarProps,
            referenceDate: rightMonth,
            onMonthChange: handleRightMonthChange,
          })}
        </Box>
      </Stack>
    </SelectedValueContext.Provider>
  );
}
